import React from 'react';
import styles from './UnitAlertsCard.module.css';
import { Alert, AlertStatus, AlarmSeverity, NotificationStats } from 'model';
import { FloTable, AlertsBadge, StatusIndicator } from 'components';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import ContentLoader from "react-content-loader";


type UnitAlertsCardProps = {
  isLoading:        boolean,
  alerts:           Alert[],
  locationStats:    NotificationStats | undefined,
  locationTimezone: string,
  onSelect:         (alert: Alert) => void,
}

export const UnitAlertsCard: React.FC<UnitAlertsCardProps> =  React.memo(({
  isLoading,
  alerts,
  locationStats,
  onSelect,
  locationTimezone,
}) => {
  const { t } = useTranslation();

  const table = alerts.map(row => ({
      ...row,
      alertName: row.displayTitle,
      statusLabel: row.status === AlertStatus.TRIGGERED ? t('Pending') : t('Resolved'),
      date: moment.utc(row.createAt).tz(locationTimezone).format('lll'),
  }));

  return (
    <div className={styles.root}>
        <Row className={styles.header}>
          <Col xs="12">
            <div className={styles.title}>{t('Alerts History')}</div>
            <div className={styles.alerts}><AlertsBadge stats={locationStats || { criticalCount: 0, infoCount: 0, warningCount: 0 }} /></div>
          </Col>
        </Row>
        <Row>
          <Col xs="12">
            { isLoading ? <AlertTableSkeleton /> : (table.length > 0) ?
            <FloTable
              className={styles.table}
              columns = {[
                { id: 'id', title: 'Id', isHidden: true, isKey: true },
                { id: 'alertName', title: t('Alert'), width: '50%', onSelect: values => onSelect(values as Alert), icon: values => (<StatusIndicator severity={values.alarm.severity as AlarmSeverity} />) },
                { id: 'statusLabel', title: t('Status'), width: '20%', color: values => (values.status === AlertStatus.RESOLVED) ? '#5ABC00': 'unset' },
                { id: 'date', title: t('Date/Time'), width: '30%' },
              ]}
              data = {table}
              height = {180}
              scrollable
              customWidths
            />
            :
            <Row className={styles.noDataContainer}><Col xs="12">{t('No Alerts')}</Col></Row>
            }
          </Col>
        </Row>
    </div>
  );
});

const textWidth = (min: number, max: number) => Math.floor(Math.random() * max) + min;
const AlertTableSkeleton: React.FC<{}> = () => (
  <ContentLoader
    height={400}
    width={'100%'}
    speed={3}
  >
    <rect x="20" y="20" rx="3" ry="3" width="100" height="20" />
    <rect x="280" y="20" rx="3" ry="3" width="80" height="20" />
    <rect x="390" y="20" rx="3" ry="3" width="100" height="20" />
    <rect x="510" y="20" rx="3" ry="3" width="75" height="20" />

    <rect x="10" y="50" rx="3" ry="3" width="680" height="1" /> 
    
    
    {
      [70, 110, 140, 170, 200].map(y => (
        <React.Fragment key={y}>
          <rect x="20" y={y} rx="3" ry="3" width={textWidth(70, 140)} height="20" />
          <rect x="280" y={y} rx="3" ry="3" width={textWidth(40, 70)} height="20" />
          <rect x="390" y={y} rx="3" ry="3" width={textWidth(30, 50)} height="20" />
          <rect x="510" y={y} rx="3" ry="3" width={textWidth(30, 50)} height="20" />
        </React.Fragment>
      ))
    }
  </ContentLoader>
)

export default UnitAlertsCard;
